// project imports
import { padding } from '@mui/system';
import './DefaultColors';
import { Theme } from '@mui/material/styles';
import { tokens } from './Tokens';

const components: any = (theme: Theme) => ({
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        boxSizing: 'border-box',
      },
      /**
       * One visible focus ring for the whole app.
       *
       * WCAG 2.2 treats focus as a requirement: 1.4.11 asks the indicator to reach
       * 3:1 against its surroundings and 2.4.11 asks that it not be obscured. The
       * theme carried no focus styling at all before this, so every control fell back
       * to whatever the browser or the component library happened to draw.
       *
       * The ring uses `secondary.main`, NOT `primary.main`. Primary is currently the
       * brand orange #FF7235, which is 2.72:1 against white and fails the 3:1 floor —
       * an orange focus ring would be invisible to the people who need it most.
       * Secondary is the purple, at 7.33:1 in light and 5.51:1 in dark. If primary
       * ever becomes the purple (see docs/design-system.md), this should follow it.
       *
       * `:focus-visible` rather than `:focus`, so a mouse click does not leave a ring
       * behind while keyboard navigation always does.
       */
      '*:focus-visible': {
        outline: `${tokens.focus.width}px solid ${theme.palette.secondary.main}`,
        outlineOffset: `${tokens.focus.offset}px`,
      },
      /**
       * MUI paints its own focus on some controls and removes the outline on others.
       * Restating the ring on the interactive roles keeps one consistent indicator
       * instead of a different one per component.
       */
      [[
        'a', 'button', '[role="button"]', '[role="tab"]', 'input', 'select',
        'textarea', 'summary', '[tabindex]:not([tabindex="-1"])',
      ].map((selector) => `${selector}:focus-visible`).join(', ')]: {
        outline: `${tokens.focus.width}px solid ${theme.palette.secondary.main}`,
        outlineOffset: `${tokens.focus.offset}px`,
      },
      html: {
        height: '100%',
        width: '100%',
      },
      a: {
        textDecoration: 'none',
      },
      body: {
        height: '100%',
        margin: 0,
        padding: 0,
      },
      '#root': {
        height: '100%',
      },
      "*[dir='rtl'] .buyNowImg": {
        transform: 'scaleX(-1)',
      },
      '.border-none': {
        border: '0px',
        td: {
          border: '0px',
        },
      },
      '.btn-xs': {
        minWidth: '30px !important',
        width: '30px',
        height: '30px',
        borderRadius: '6px !important',
        padding: '0px !important',
      },
      '.hover-text-primary:hover .text-hover': {
        color: theme.palette.primary.main,
      },
      '.hoverCard:hover': {
        scale: '1.01',
        transition: ' 0.1s ease-in',
      },
      '.signup-bg': {
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
      },
      '.MuiBox-root': {
        borderRadius: theme.shape.borderRadius,
      },
      '.MuiCardHeader-action': {
        alignSelf: 'center !important',
      },
      '.emoji-picker-react .emoji-scroll-wrapper': {
        overflowX: 'hidden',
      },
      '.scrollbar-container': {
        borderRight: '0 !important',
      },
      '.theme-timeline .MuiTimelineOppositeContent-root': {
        minWidth: '90px',
      },
      '.MuiAlert-root .MuiAlert-icon': {
        color: 'inherit!important',
      },
      '.MuiTimelineConnector-root': {
        width: '1px !important',
      },
      ' .simplebar-scrollbar:before': {
        background: `${theme.palette.grey[300]} !important`,
      },
      '@keyframes gradient': {
        '0%': {
          backgroundPosition: '0% 50%',
        },
        '50%': {
          backgroundPosition: ' 100% 50%',
        },
        '100% ': {
          backgroundPosition: ' 0% 50%',
        },
      },
      '.rounded-bars .apexcharts-bar-series.apexcharts-plot-series .apexcharts-series path': {
        clipPath: 'inset(0 0 5% 0 round 20px)',
      },
    },
  },
  MuiButtonGroup: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        ':before': {
          backgroundColor: theme.palette.grey[100],
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        // border: `1px solid ${theme.palette.divider}`,
        backgroundImage: 'none',
      },
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: {
        borderColor: theme.palette.divider,
      },
    },
  },
  MuiFab: {
    styleOverrides: {
      root: {
        boxShadow: 'none',
      },
      sizeSmall: {
        width: 30,
        height: 30,
        minHeight: 30,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        boxShadow: 'none',
      },
      text: {
        padding: '5px 15px',
      },
      textPrimary: {
        backgroundColor: theme.palette.primary.light,
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      },
      textSecondary: {
        backgroundColor: theme.palette.secondary.light,
        '&:hover': {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        },
      },
      textSuccess: {
        backgroundColor: theme.palette.success.light,
        '&:hover': {
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
        },
      },
      textError: {
        backgroundColor: theme.palette.error.light,
        '&:hover': {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        },
      },
      textInfo: {
        backgroundColor: theme.palette.info.light,
        '&:hover': {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.info.contrastText,
        },
      },
      textWarning: {
        backgroundColor: theme.palette.warning.light,
        '&:hover': {
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
        },
      },
      outlinedPrimary: {
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
      },
      outlinedSecondary: {
        '&:hover': {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        },
      },
      outlinedError: {
        '&:hover': {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
        },
      },
      outlinedSuccess: {
        '&:hover': {
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
        },
      },
      outlinedInfo: {
        '&:hover': {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.info.contrastText,
        },
      },
      outlinedWarning: {
        '&:hover': {
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
        },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
      },
      title: {
        fontSize: '1.125rem',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        width: '100%',
        padding: '15px',
        backgroundImage: 'none',
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '10px 16px',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: '8px 16px'
      },
      head: {
        padding: '8px 16px'
      },
      body: {
        padding: '8px 16px'
      }
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child td': {
          borderBottom: 0,
        },
      },
    },
  },
  MuiGridItem: {
    styleOverrides: {
      root: {
        paddingTop: '30px',
        paddingLeft: '12px !important',
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.grey[200],
        borderRadius: '6px',
      },
    },
  },
  MuiTimelineConnector: {
    styleOverrides: {
      root: {
        backgroundColor: theme.palette.divider,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: theme.palette.divider,
      },
    },
  },

  /**
   * Links carry their own colour, because the browser's does not survive dark mode.
   *
   * An unstyled `<a>` falls back to the user-agent blue `#0000EE`. On the light page
   * that is 9.40:1 and perfectly readable; on the dark dialog surface `#202c33` it is
   * 1.52:1 — the text is there, and nobody can see it. Nothing in the app declared
   * `color-scheme`, so the browser never swapped in its dark-mode link colour either.
   *
   * The colour is `secondary.main`, NOT `primary.main`, for the same reason the focus
   * ring above is: primary is the brand orange, 2.72:1 on white, which fails 4.5:1 for
   * body text in light mode. Secondary passes in both — 7.33:1 light, 4.51:1 dark.
   *
   * The colour is a `defaultProp` rather than a `styleOverrides.root` rule, because MUI
   * derives `text-decoration-color` from the `color` PROP: overriding only the root rule
   * repainted the text and left the underline brand orange. MUI already underlines every
   * link by default (WCAG 1.4.1 — not signalled by colour alone), so nothing restates it
   * here; linkColor.test.tsx measures the rendered underline and catches it if that moves.
   */
  MuiLink: {
    defaultProps: {
      color: 'secondary.main',
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        fontSize: '0.75rem',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      filledSuccess: {
        color: theme.palette.success.contrastText,
      },
      filledInfo: {
        color: theme.palette.info.contrastText,
      },
      filledError: {
        color: theme.palette.error.contrastText,
      },
      filledWarning: {
        color: theme.palette.warning.contrastText,
      },
      standardSuccess: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.main,
      },
      standardError: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.main,
      },
      standardWarning: {
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.main,
      },
      standardInfo: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.main,
      },
      outlinedSuccess: {
        borderColor: theme.palette.success.main,
        color: theme.palette.success.main,
      },
      outlinedWarning: {
        borderColor: theme.palette.warning.main,
        color: theme.palette.warning.main,
      },
      outlinedError: {
        borderColor: theme.palette.error.main,
        color: theme.palette.error.main,
      },
      outlinedInfo: {
        borderColor: theme.palette.info.main,
        color: theme.palette.info.main,
      },
      successIcon: {
        color: theme.palette.info.main,
      },
    },
  },
  MuiSwitch: {
    // Thirty-six files render a bare MUI Switch and only sixteen use CustomSwitch, so the
    // fix that component received reached under a third of the switches in the app. MUI's
    // own default paints the checked track at 50% opacity, which measured 1.62:1 in light
    // and 2.70:1 in dark — a control whose two states are told apart mostly by guessing.
    //
    // Reached from the theme so every Switch gets it, themed wrapper or not. The states
    // are separated by hue and by thumb position rather than by luminance; what each one
    // must do is stand out from the surface behind it, and both now do.
    styleOverrides: {
      track: {
        backgroundColor: theme.palette.grey[400],
        opacity: 1,
      },
      switchBase: {
        // Opacity only. MUI already paints this track `palette[color].main` per colour,
        // so the hue was never the problem — the 0.5 opacity was, and it is the whole
        // reason a checked switch was unreadable. Two earlier drafts got this wrong:
        // hardcoding the primary ramp repainted every non-primary switch, and
        // `currentColor` resolves against the track's own inherited colour, not the
        // sibling switch base MUI sets the palette colour on.
        '&.Mui-checked + .MuiSwitch-track': {
          opacity: 1,
        },
        // Primary alone steps down the ramp: the brand orange is 2.72:1 on white, under
        // the 3:1 a control needs, and `primary.dark` is 3.44:1 light / 4.91:1 dark.
        '&.MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
  },
  /**
   * The placeholder, which MUI draws as `currentColor` faded rather than as a colour.
   *
   * That fade is `opacity: 0.42` in light and `0.5` in dark, applied to `text.primary`.
   * Flattened against the surface behind it, the light default lands at 2.92:1 and the
   * dark one at 4.20:1 — both under the 4.5:1 that 1.4.3 asks of text, and the light one
   * badly. A placeholder often carries the only hint of a field's expected format, so it
   * is not decoration that can be excused.
   *
   * Fixed with a token rather than a higher opacity. `text.secondary` is what "quieter
   * than the value, still readable" already means in this palette, and it measures
   * 15.98:1 in light and 4.68:1 in dark. An opacity tuned to clear the floor would have
   * to be re-tuned the next time either surface moves.
   *
   * On `MuiInputBase`, so it reaches the standard and filled variants too, not only the
   * outlined one the app mostly uses.
   */
  MuiInputBase: {
    styleOverrides: {
      input: {
        '&::placeholder': {
          color: theme.palette.text.secondary,
          opacity: 1,
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        // Every text field in the application takes its boundary from here, and every
        // one of them was under the 3:1 WCAG 1.4.11 asks of a control: 1.20:1 in dark,
        // 1.27:1 in light. Hover did not even change in light mode — the same grey[300]
        // on both sides — so the field neither showed its edge nor reacted to a pointer.
        //
        // grey[400] and grey[500] resolve per mode on their own, which also retires the
        // `palette.mode` ternary that used to pick a different SLOT per mode here.
        // Measured: 4.68:1 / 3.29:1 at rest, 7.21:1 / 5.48:1 on hover.
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.grey[400],
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.grey[500],
        },
        '&.MuiInputBase-multiline': {
          height: 'auto',
          padding: 1
        }
      },
      input: {
        padding: '8px 14px',
        '&.MuiInputBase-inputMultiline': {
          height: 'auto !important',
          minHeight: '40px !important',
          padding: '8px 14px'
        }
      },
      inputSizeSmall: {
        padding: '8px 14px',
        '&.MuiInputBase-inputMultiline': {
          height: 'auto !important',
          minHeight: '40px !important',
          padding: '8px 14px'
        }
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      size: 'small'
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          height: '40px'
        },
        '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
          height: 'auto'
        }
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        color: theme.palette.background.paper,
        background: theme.palette.text.primary,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderColor: `${theme.palette.divider}`,
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.25rem',
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        boxShadow: 'rgb(145 158 171 / 30%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px',
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: {
        '@media (min-width:1200px)': {
          maxWidth: '100%',
        },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          padding: '0px 9px'
        }
      }
    }
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 'auto',
        padding: '12px 16px 10px 16px !important'
      }
    }
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: '48px !important',
        paddingLeft: '5px !important',
        paddingRight: '24px !important',
        '@media (min-width: 600px)': {
          minHeight: '48px !important',
          paddingLeft: '5px !important',
          paddingRight: '24px !important'
        }
      }
    }
  },
  MuiGrid: {
    styleOverrides: {
      root: {
        '& > .MuiGrid-item': {
          paddingLeft: '12px !important',
          paddingTop: '16px !important'
        }
      }
    }
  },

});
export default components;
